import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Card, Input } from '../shared/UI';
import { useT } from '../../i18n';
import api from '../../utils/api';

const STEPS = ['company', 'client', 'document', 'sent'];

// Getting-started card at the top of the dashboard. Renders nothing for accounts
// that have finished, dismissed it, or were already busy before it existed.
export default function OnboardingChecklist({ show }) {
  const navigate = useNavigate();
  const { t } = useT();
  const [state, setState] = useState(null);
  const [company, setCompany] = useState({ name: '', phone: '', logoUrl: '' });
  const [saving, setSaving] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);

  const load = () => api.get('/onboarding').then(r => setState(r.data)).catch(() => setState(null));
  useEffect(() => { load(); }, []);

  if (!state?.visible) return null;

  const current = STEPS.find(s => !state.steps[s]); // the first unfinished step is the open one

  const handleLogo = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setCompany(c => ({ ...c, logoUrl: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveCompany = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: company.name.trim(), phone: company.phone };
      if (company.logoUrl) body.logoUrl = company.logoUrl;
      await api.put('/company', body);
      show(t('onboarding.companySaved'));
      await load();
    } catch { show(t('settings.errorSaving'), 'error'); }
    finally { setSaving(false); }
  };

  const sample = async () => {
    if (state.sampleDocId) return navigate(`/documents/${state.sampleDocId}`);
    setSampleBusy(true);
    try {
      const { data } = await api.post('/onboarding/sample');
      navigate(`/documents/${data._id}`);
    } catch (err) { show(err.response?.data?.message || t('onboarding.sampleFailed'), 'error'); setSampleBusy(false); }
  };

  const dismiss = async () => {
    setState(s => ({ ...s, visible: false }));
    api.post('/onboarding/dismiss').catch(() => {});
  };

  const actions = {
    company: (
      <form onSubmit={saveCompany} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
        <Input label={t('onboarding.companyName')} required value={company.name} style={{ flex: '2 1 200px' }}
          onChange={e => setCompany(c => ({ ...c, name: e.target.value }))} placeholder="Kedai Rahman Enterprise" />
        <Input label={t('onboarding.phone')} value={company.phone} style={{ flex: '1 1 140px' }}
          onChange={e => setCompany(c => ({ ...c, phone: e.target.value }))} placeholder="03-1234 5678" />
        <div style={{ flex: '1 1 200px' }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('onboarding.logo')}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {company.logoUrl && <img src={company.logoUrl} alt="" style={{ height: 34, maxWidth: 70, objectFit: 'contain' }} />}
            <input type="file" accept="image/*" onChange={handleLogo} style={{ fontSize: 12, color: 'var(--text)', maxWidth: 190 }} />
          </div>
        </div>
        <Btn type="submit" variant="primary" disabled={saving}>{saving ? t('common.saving') : t('onboarding.saveCompany')}</Btn>
        <Btn type="button" variant="ghost" onClick={() => navigate('/settings')}>{t('onboarding.moreSettings')}</Btn>
      </form>
    ),
    client: <div style={actionRow}><Btn variant="primary" onClick={() => navigate('/clients')}>+ {t('onboarding.addClient')}</Btn></div>,
    document: (
      <div style={actionRow}>
        <Btn variant="primary" onClick={() => navigate('/documents/new?type=invoice')}>🧾 {t('onboarding.newInvoice')}</Btn>
        <Btn variant="ghost" onClick={() => navigate('/documents/new?type=quotation')}>📋 {t('onboarding.newQuotation')}</Btn>
      </div>
    ),
    sent: <div style={actionRow}><Btn variant="primary" onClick={() => navigate('/documents')}>{t('onboarding.openDocs')}</Btn></div>,
  };

  return (
    <Card style={{ marginBottom: 24, border: '1px solid var(--accent)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--heading)' }}>🚀 {t('onboarding.title')}</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{t('onboarding.subtitle')}</p>
        </div>
        <button onClick={dismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', fontSize: 12, fontFamily: 'inherit', padding: 0 }}>
          {t('onboarding.dismiss')} ×
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 6px' }}>
        <div style={{ flex: 1, height: 6, borderRadius: 6, background: 'var(--border)', overflow: 'hidden' }}>
          <div style={{ width: `${(state.done / state.total) * 100}%`, height: '100%', background: 'var(--success)', transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t('onboarding.progress', { done: state.done, total: state.total })}</span>
      </div>

      {STEPS.map((step, i) => {
        const done = state.steps[step];
        const open = step === current;
        return (
          <div key={step} style={{ display: 'flex', gap: 12, padding: '12px 0', borderTop: i ? '1px solid var(--border-light)' : 'none' }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700,
              background: done ? 'var(--success)' : open ? 'var(--navy)' : 'transparent',
              color: done || open ? '#fff' : 'var(--text-subtle)',
              border: done || open ? 'none' : '1.5px solid var(--border)',
            }}>{done ? '✓' : i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: done ? 'var(--text-subtle)' : 'var(--text)', textDecoration: done ? 'line-through' : 'none' }}>
                {t(`onboarding.s_${step}`)}
              </div>
              {open && <>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{t(`onboarding.d_${step}`)}</div>
                {actions[step]}
              </>}
            </div>
          </div>
        );
      })}

      {/* One-click sample */}
      <div style={{ marginTop: 8, padding: '14px 16px', borderRadius: 10, background: 'var(--hover-bg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>✨ {t('onboarding.sampleTitle')}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 }}>{t('onboarding.sampleBody')}</div>
        </div>
        <Btn variant="accent" onClick={sample} disabled={sampleBusy}>
          {state.sampleDocId ? t('onboarding.sampleOpen') : t('onboarding.sampleCreate')}
        </Btn>
      </div>
    </Card>
  );
}

const actionRow = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 };
